/**
 * 專案唯一日期／時間格式契約：
 * - API／URL query／serialize（僅日期）：YYYY-MM-DD
 * - UI 顯示（僅日期）：YYYY/MM/DD（月日補零）
 * - UI 顯示（區間）：YYYY/MM/DD ~ YYYY/MM/DD；起迄同日僅顯示單日
 * - UI 顯示（含時間）：YYYY/MM/DD HH:mm:ss
 * - 使用者手打：允許多種 → parseUserDateToApi（含 8 碼 YYYYMMDD）
 *
 * 業務檔禁止直接 import dayjs；禁止 toLocaleDateString 當日期顯示。
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const API_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'YYYY/MM/DD';
const API_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const DISPLAY_DATETIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';
const COMPACT_DATE_FORMAT = 'YYYYMMDD';

/** 嚴格多格式：單數月／日可解析，輸出一律雙位 YYYY-MM-DD */
const USER_DATE_FORMATS = [
  'YYYY/M/D',
  'YYYY/MM/DD',
  'YYYY/M/DD',
  'YYYY/MM/D',
  'YYYY-M-D',
  'YYYY-MM-DD',
  'YYYY-M-DD',
  'YYYY-MM-D',
];

/** 區間分隔：~／～／至／全形－／en／em dash；空白包住的 ` - ` */
const RANGE_SEPARATOR_PATTERN = /\s*(?:~|～|至|－|–|—|\s-\s)\s*/;

/**
 * 使用者日期 → API `YYYY-MM-DD`。
 * 空字串 → null；無法解析 → null。
 * 無分隔僅接受恰好 8 碼 `YYYYMMDD`（`2026126` 等模糊短數字 → null）。
 */
export function parseUserDateToApi(input) {
  if (input == null) {
    return null;
  }
  const trimmed = String(input).trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d+$/.test(trimmed)) {
    if (trimmed.length === 8) {
      const compact = dayjs(trimmed, COMPACT_DATE_FORMAT, true);
      if (compact.isValid()) {
        return compact.format(API_FORMAT);
      }
    }
    return null;
  }

  for (const format of USER_DATE_FORMATS) {
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) {
      return parsed.format(API_FORMAT);
    }
  }

  return null;
}

/**
 * 單一 input 區間／單日解析。
 * @returns {{ from: string|null, to: string|null, mode: 'empty'|'single'|'range'|'invalid' }}
 */
export function parseUserDateRangeInput(input) {
  if (input == null || String(input).trim() === '') {
    return { from: null, to: null, mode: 'empty' };
  }

  const trimmed = String(input).trim();
  const asSingle = parseUserDateToApi(trimmed);
  if (asSingle) {
    return { from: asSingle, to: null, mode: 'single' };
  }

  const parts = trimmed.split(RANGE_SEPARATOR_PATTERN).map((part) => part.trim()).filter(Boolean);
  if (parts.length !== 2) {
    return { from: null, to: null, mode: 'invalid' };
  }

  const fromApi = parseUserDateToApi(parts[0]);
  const toApi = parseUserDateToApi(parts[1]);
  if (!fromApi || !toApi) {
    return { from: null, to: null, mode: 'invalid' };
  }

  if (fromApi > toApi) {
    return { from: toApi, to: fromApi, mode: 'range' };
  }

  return { from: fromApi, to: toApi, mode: 'range' };
}

/**
 * API `YYYY-MM-DD` → 顯示用 `YYYY/MM/DD`（月日補零）。
 * 無效 → 空字串。
 */
export function formatApiDateDisplay(api) {
  if (api == null || String(api).trim() === '') {
    return '';
  }
  const parsed = dayjs(String(api).trim(), API_FORMAT, true);
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.format(DISPLAY_FORMAT);
}

/**
 * 區間顯示：`YYYY/MM/DD ~ YYYY/MM/DD`；起迄同日僅單日；僅 from → 單日斜線；皆空 → ''。
 */
export function formatApiDateRangeDisplay({ from, to } = {}) {
  const fromDisplay = formatApiDateDisplay(from);
  const toDisplay = formatApiDateDisplay(to);
  if (fromDisplay && toDisplay) {
    if (from === to) {
      return fromDisplay;
    }
    return `${fromDisplay} ~ ${toDisplay}`;
  }
  return fromDisplay || toDisplay || '';
}

/**
 * 時間戳 → `YYYY/MM/DD HH:mm:ss`。
 * 接受 `YYYY-MM-DD HH:mm:ss` 與 ISO（如 `2026-01-05T08:00:00.000Z`）。
 * 空／無效 → 空字串。
 */
export function formatApiDateTimeDisplay(value) {
  if (value == null || String(value).trim() === '') {
    return '';
  }
  const trimmed = String(value).trim();
  const strict = dayjs(trimmed, API_DATETIME_FORMAT, true);
  if (strict.isValid()) {
    return strict.format(DISPLAY_DATETIME_FORMAT);
  }
  const loose = dayjs(trimmed);
  if (!loose.isValid()) {
    return '';
  }
  return loose.format(DISPLAY_DATETIME_FORMAT);
}

/** `formatApiDateDisplay` 後空則 `'-'`。 */
export function formatDateOrDash(value) {
  return formatApiDateDisplay(value) || '-';
}

/** `formatApiDateTimeDisplay` 後空則 `'-'`。 */
export function formatDateTimeOrDash(value) {
  return formatApiDateTimeDisplay(value) || '-';
}

/** 嚴格檢查是否為 `YYYY-MM-DD`。 */
export function isValidApiDate(api) {
  if (api == null || String(api).trim() === '') {
    return false;
  }
  return dayjs(String(api).trim(), API_FORMAT, true).isValid();
}

/**
 * API `YYYY-MM-DD` 加整年。無效或空 → 空字串。
 * 2/29 加一年落在非閏年時，對齊 dayjs 收到 2/28。
 */
export function addYearsToApiDate(api, years = 1) {
  if (!isValidApiDate(api)) {
    return '';
  }
  return dayjs(String(api).trim(), API_FORMAT, true).add(years, 'year').format(API_FORMAT);
}

/**
 * 列表日期篩選：空 → 清掉；可解析 → 寫入 YYYY-MM-DD；無法解析 → 不更新。
 */
export function commitParsedDateFilter(value, onCommit) {
  if (!value) {
    onCommit(undefined);
    return;
  }
  const api = parseUserDateToApi(value);
  if (api) {
    onCommit(api);
  }
}

/**
 * 列表區間／單日篩選 commit。
 * @param {string|undefined|null} value
 * @param {{ onSingle?: (api: string) => void, onRange?: ({from,to}) => void, onClear?: () => void }} handlers
 */
export function commitParsedDateRangeFilter(value, {
  onSingle,
  onRange,
  onClear,
} = {}) {
  const parsed = parseUserDateRangeInput(value);
  if (parsed.mode === 'empty') {
    onClear?.();
    return;
  }
  if (parsed.mode === 'single' && parsed.from) {
    onSingle?.(parsed.from);
    return;
  }
  if (parsed.mode === 'range' && parsed.from && parsed.to) {
    onRange?.({ from: parsed.from, to: parsed.to });
  }
}

/** API 字串 ↔ Date（日曆用；無效 → undefined）。 */
export function apiDateToDate(api) {
  if (!isValidApiDate(api)) {
    return undefined;
  }
  const parsed = dayjs(String(api).trim(), API_FORMAT, true);
  return parsed.toDate();
}

export function dateToApiDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return dayjs(date).format(API_FORMAT);
}
